import { hasRel } from "@/helpers/rels"
import { interpolate, isInterpolated } from '@/helpers/strings'
import { solveExpressionProperty } from '@/helpers/expressions'

/**
 * Executa uma ação aplicando os padrões de navegação e regras de negócio.
 *
 * @param {VueComponent} component
 *    O componente que contém a ação sendo executada.
 * @param {*} opts
 *    As opções da ação em execução.
 *    Este objeto deve conter a seguinte estrutura:
 *    {
 *      link: <Link>    // O link de navegação associado com a ação.
 *      form: <Form>    // O formulário associado com a ação.
 *      selection: []   // Os registros selecionados enviados com a ação.
 *    }
 * @param {*} context
 *    O contexto da execução da ação.
 *    Este objeto deve conter a seguinte estrutura:
 *    {
 *      page: <Entity>  // A página (ou entidade) associada com a ação.
 *      data: <Set>     // Os dados associados com a ação.
 *      row: <Row>      // A linha (ou registro) específica associada com a ação, se aplicável.
 *    }
 */
export function performAction(component, opts, context) {
  let link = opts?.link || {}
  let form = opts?.form || {}
  let fields = component?.fields || []
  let rule = link?.rule || []

  if (link.expression) {
    solveExpressionProperty(link, link, (context || {}))
  }

  // Abrindo uma página externa se aplicável.
  if (link?.href) {
    const href = applyFormat(link.href)
    opts.link = link = { ...link, href }
    window.open(link.href, link.href)
    return
  }

  if (link?.eref) {
    const eref = applyFormat(link.eref)
    opts.link = link = { ...link, eref }
  }

  if (form && fields) {
    // Capturando campos do formulário.
    // Note que campos sem nome ou cujo nome se inicia com ponto "." são ignorados.
    fields
        .filter(field => field.name && !field.name.startsWith('.'))
        .forEach(field => form[field.name] = field.value)
    // Aplicando templates nos campos do formulário.
    applyTemplate(form, fields)
  }

  // Retirando os dados atuais do cache se solicitado...
  const refetch = rule?.some(rule => rule.toLowerCase() === 'refetch')
  if (refetch) {
    component.$close(link)
  }

  // Filtrando apenas registros selecionáveis, se houver.
  if (opts.selection) {
    // Se selection não for array o convertemos em array.
    if (!Array.isArray(opts.selection)) {
      opts.selection = [opts.selection]
    }
    opts.selection = opts.selection?.filter(row => !isRowNonSelectable(row));
  }

  // Abrindo link de navegação se solicitado...
  const browse = rule?.some(rule => rule === 'Browse')
  if (browse) {
    if (opts.selection) {
      link.data = opts.selection
    }
    component.$go(link)
    return
  }

  if ('request' in component) {
    // Acionando o popup de ação.
    component.request = opts
    return
  }

  // Propagando a ação para o componente pai.
  component.$emit('perform', opts)
}

function isRowNonSelectable(row) {
  return row?.['@row']?.nonSelectable || hasRel(row?.['@row'], "Summary")
}

function applyFormat(text) {
  return isInterpolated(text) ? interpolate(text, this.context) : text
}

function applyTemplate(targetForm, sourceFields) {
  // substituindo parametros nas formas `{ @parametro }` e `{{ @parametro }}`...
  Object.entries(targetForm).forEach(([key, value]) => {
    if (typeof value !== 'string' || !value?.includes('@'))
      return

    const match = /{+ *@(.*?) *}+/.exec(value)
    if (!match)
      return

    const fieldName = match[1]
    const fieldValue = sourceFields.filter(
      field => field.name.toLowerCase() === fieldName.toLowerCase()
    ).map(field => field.value)[0]

    targetForm[key] = fieldValue
  })
}
